import { ref, watch, onMounted } from "@vue/composition-api";
import { getTextHeight, getTextWidth } from "../../services/Canvas2DContext";
import { DefaultConfigs, IPadProConfigs } from "../../services/DefaultConfigs";
import TickLabelFormatService from "../../services/TickLabelFormatService";

const BAR_LABEL_SHIFT = 5;

function getBarLabelValue(bar, hideZeroValues) {
  const value = (bar?.label?.text ?? '').toString();

  return (hideZeroValues && value === '0') ? '' : value;
}

function getBarLabelY(barY) {
  return barY - BAR_LABEL_SHIFT;
}

export default function useBarChartBarGroups(configRef, dataRef, availableWidthRef, availableHeightRef, xRef, yRef, xBandwidthRef, isIPadProRef) {
  const groups = ref([]);

  const isStacked = configRef?.value?.type === "bar-stacked";

  const updateGroups = () => {
    if (!configRef?.value?.xAxis || availableHeightRef.value <= 0 || !xRef?.value || !yRef?.value) {
      groups.value = [];
      return;
    }

    const moveOverridesOnAxis = configRef.value.xAxis.overridesOnAxis;
    const lowestY = yRef.value.domain()[yRef.value.domain().length - 1];

    const newGroups = dataRef.value.map(group => {
      const groupWidth = xBandwidthRef.value;
      const groupX = xRef.value(group.x);
      const font = isIPadProRef?.value ? IPadProConfigs.BarChart.barLabel.font : DefaultConfigs.BarChart.barLabel.font;

      const processedResult = {
        bars: [],
        overrides: [],
      };

      let stackedY = 0;

      const labelHeightCache = [];

      group.bars.forEach((bar, i) => {
        const shouldPreppendOverride = bar.label.override && !moveOverridesOnAxis;

        // The "sup" part will always be displayed, if present
        let combinedLabel = bar.label.sup ? `<sup>${bar.label.sup}</sup>`: "";

        // if `hideValuesAboveBars` flag is falsy, we conditionally preppend the `override`
        // and always preppend the actual label text
        if (!configRef.value.hideValuesAboveBars) {
          combinedLabel = (shouldPreppendOverride ? bar.label.override + '<br>' : '')
            + getBarLabelValue(bar, configRef.value.xAxis?.hideZeroValues)
            + combinedLabel;
        }

        let height = Math.max(0, availableHeightRef.value - yRef.value(bar.y));
        const hasAnyLabel = !!combinedLabel;

        // Alternate bar y value based on using a stacked chart or not.
        let width = (group.bars.length > 0) ? groupWidth / group.bars.length : groupWidth;
        let barX = groupX + width * i;
        let barY = bar.y;
        let label = TickLabelFormatService.preFormatTickLabel(combinedLabel, font);

        if (isStacked) {
          // Set alternate values needed for stacked chart.
          width = groupWidth;
          barX = groupX;
          stackedY += bar.y;
          barY = stackedY;

          // Temporary: For the stacked bars, use the cumulative height as the label for the top bar
          // This should be replaced with a specific group label added to the chart config.
          if (i === group.bars.length - 1) {
            label = stackedY;
          } else {
            label = '';
          }
        }

        const y = yRef.value(barY);
        const labelHeight = getTextHeight(font, label);
        let labelY = getBarLabelY(y);
        let requiresLabelConnector;

        if (hasAnyLabel) {
          let previousBar;

          if (processedResult.bars.length) {
            previousBar = processedResult.bars[processedResult.bars.length - 1];
          }

          if (previousBar) {
            const previousLabelWidth = getTextWidth(previousBar.font, previousBar.label);
            const labelTakesMoreSpaceThanTheBar = previousLabelWidth > previousBar.width;
            if (labelTakesMoreSpaceThanTheBar) {
              const previousLabelY = getBarLabelY(previousBar.y);
              const previousLabelHeight = labelHeightCache[labelHeightCache.length - 1];
              const previousLabelCollidesWithCurrentLabel =
                (labelY >= previousLabelY && labelY <= previousLabelY + previousLabelHeight) ||
                (previousLabelY >= labelY && previousLabelY <= labelY + labelHeight);

              if (previousLabelCollidesWithCurrentLabel) {
                requiresLabelConnector = true;
                labelY = previousLabelY - previousLabelHeight - BAR_LABEL_SHIFT;
              }
            }
          }
        }

        // Adding to the cache *only* after the cache was potentially used, to ensure
        // the current label height is not returned as the previous label heighy
        labelHeightCache.push(labelHeight);

        processedResult.bars.push({
          id: `bar-${group.x}-${bar.name}`,
          x: barX,
          y,
          fill: bar.color,
          stroke: bar.color,
          width,
          height,
          requiresLabelConnector,
          labelY,
          label,
          font,
        });

        const shouldAppendOverride = moveOverridesOnAxis && bar.label.override;
        processedResult.overrides.push(shouldAppendOverride ? {
          id: `override-${groupX}-${bar.name}`,
          x: (groupX + width * i) + (width / 2),
          y: yRef.value(lowestY) + getTextHeight(font),
          label: TickLabelFormatService.preFormatTickLabel(bar.label.override, font),
          font,
        } : null);
      });

      return {
        id: `bar-group-${group.x}`,
        ...processedResult,
      };
    });

    groups.value = newGroups;
  }

  watch([configRef, dataRef, availableWidthRef, availableHeightRef, xRef, yRef, xBandwidthRef, isIPadProRef], updateGroups);

  onMounted(updateGroups);

  return { groups };
}
